/**
 * onboardInvite.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import moment from "moment-timezone";
import * as React from "react";
import { Location, NavigateFunction, useLocation, useNavigate } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import TextField from "@mui/material/TextField";

import { AuthContext } from "./auth.context";

import { SCAvatar } from "../components/avatar.component";
import { LoginModal } from "./login.component";
import { MfaSetupComponent } from "./mfaSetup.component";

import { AccountInvite, User, PermissionLevel } from "../models/auth";
import { authServiceFactory, NewUser } from "../services/auth.service";
import { AlreadyClaimedError, EarlyEnrollmentError, ExpiredTokenError } from "../models/error";
import { Brand } from "../models/brand";

export function ProcessInviteWrapper(): React.ReactElement<any, any> {
  const navigate = useNavigate();
  const location = useLocation();
  return <ProcessInviteContainer navigate={navigate} location={location} />;
}

type InputErrors = {
  [K in keyof NewUser]: string
}
interface Props {
  navigate: NavigateFunction
  location: Location
}
interface State {
  loading: boolean,
  saving: boolean,
  invalidBrowser: boolean,
  invalidToken: boolean,
  haveInternalAuthenticator: boolean,
  token: string,
  tokenType: "jwt" | "code",
  doUserCreation: boolean,
  activeStep: number,
  errMsg: string,
  invite: AccountInvite | null,
  existingUser: User | null,
  avatarText: string,
  newUser: NewUser,
  errors: InputErrors,
  openLoginModal: boolean,
  showingOTPView: boolean,
  showExistingUserPostLogin: boolean,
  newUserID: string,
  isIOS: boolean,
  productName: string
}

export class ProcessInviteContainer extends React.Component<Props, State> {
  static contextType = AuthContext;
  declare context: React.ContextType<typeof AuthContext>;

  //private fnameInput: HTMLInputElement | undefined;
  private lnameInput: HTMLInputElement | undefined;
  private emailInput: HTMLInputElement | undefined;
  private passInput: HTMLInputElement | undefined;
  private confirmInput: HTMLInputElement | undefined;

  private lastStep: number;

  constructor(props: Props) {
    super(props);
    this.lastStep = 1;

    this.state = {
      loading: false,
      saving: false,
      invalidBrowser: false,
      invalidToken: false,
      haveInternalAuthenticator: true,
      token: "",
      tokenType: "jwt",
      doUserCreation: false,
      activeStep: 0,
      errMsg: "",
      invite: null,
      existingUser: null,
      avatarText: "",
      newUser: {
        firstname: "",
        lastname: "",
        emails: [""],
        perms: {
          profile: PermissionLevel.NONE
        },
        created: moment(),
        pass: "",
        confirmpass: ""
      },
      errors: {
        firstname: "",
        lastname: "",
        emails: "",
        perms: "",
        created: "",
        pass: "",
        confirmpass: ""
      },
      openLoginModal: false,
      showingOTPView: false,
      showExistingUserPostLogin: false,
      newUserID: "",
      isIOS: false,
      productName: "Mercury Workspace"
    };

    this.finishedLogin = this.finishedLogin.bind(this);
    this.claimAsCurrentUser = this.claimAsCurrentUser.bind(this);
    this.onOtherUserLogin = this.onOtherUserLogin.bind(this);
    this.doAccountCreation = this.doAccountCreation.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.goToApp = this.goToApp.bind(this);
  }

  async componentDidMount(): Promise<void> {
    let token: string | null = null;
    let tokenType: "jwt" | "code" = "jwt";

    this.setState({
      productName: this.context.brand === Brand.WORKSPACE ? "Mercury Workspace" : "GLYPH"
    });

    if (this.props.location.search) {
      const params = new URLSearchParams(this.props.location.search.substring(1));
      if (params.has("code")) {
        token = params.get("code");
        tokenType = "code";
      } else if (params.has("token")) {
        token = params.get("token");
        tokenType = "jwt";
      }
    }
    if (!token) {
      this.setState({errMsg: "Missing Invitation"});
      return;
    }
    if (!("credentials" in navigator)) {
      this.setState({
        errMsg: "We only support browsers that provide newer authentication mechanisms",
        invalidBrowser: true
      });
      return;
    }

    try {
      const authService = authServiceFactory();
      let codeData: {invite: AccountInvite, token: string} | null = null;
      if (tokenType === "code") {
        codeData = await authService.validateInviteCode(token);  
        token = codeData.token;
      }
      const isIOS = !!navigator.platform.match(/(iPhone)|(iPod)|(iPad)/);
      this.setState({loading: true, token: token, tokenType: tokenType, isIOS: isIOS});
      const [invite, user, haveInternalAuthenticator] = await bluebird.all([
        codeData ? codeData.invite : authService.validateToken(token),
        authService.accessToken ? authService.getMe() : bluebird.resolve(null),
        PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable ?
          PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable() :
          bluebird.resolve(false)
      ]);

      this.setState({
        invite: invite,
        haveInternalAuthenticator: haveInternalAuthenticator,
        existingUser: user,
        avatarText: user ? authService.makeAvatarText(user): "",
        newUser: {...this.state.newUser, emails: [invite.email]}
      });
    } catch (err) {
      console.error("Error setting up token:", err);
      if (err instanceof ExpiredTokenError) {
        this.setState({
          invalidToken: true,
          errMsg: `This token has expired. Contact your administrator to have to
            have them send you fresh one.`
        });
      } else if (err instanceof EarlyEnrollmentError) {
        this.setState({
          invalidToken: true,
          errMsg: `Enrollment for this onboarding campaign hasn't begun yet.
            Please, come back and try again later. Thanks!`
        });
      } else if (err instanceof AlreadyClaimedError) {
        this.setState({
          invalidToken: true,
          errMsg: "This invite has already been claimed."
        });
      } else {
        this.setState({errMsg: (err as Error).message});
      }
    } finally {
      this.setState({loading: false});
    }
  }

  updateValue(event: React.SyntheticEvent): void {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const newValue = inputElem.value;
    const update: any = propName === "email" ?
      {...this.state.newUser, emails: [newValue.toLowerCase().trim()]} :
      {...this.state.newUser, [propName]: newValue};
    this.setState({newUser: update});

    let msg = "";
    if (propName === "pass") {
      msg = authServiceFactory().checkPWRequirements(newValue);
    } else if (propName === "confirmpass") {
      if (!newValue) {
        return;
      } else if (newValue !== this.state.newUser.pass) {
        msg = "Password mismatch";
      }
    }
    this.setState({errors: {...this.state.errors, [propName]: msg}});
  }

  private onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    let propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    let msg = "";
    switch (propName) {
      case "firstname":
        if (!this.state.newUser.firstname) {
          msg = "Enter a first name."
        }
        break;
      case "lastname":
        if (!this.state.newUser.lastname) {
          msg = "Enter a last name."
        }
        break;
      case "email":
        propName = "emails";
        if (!this.state.newUser.emails || this.state.newUser.emails.length <= 0) {
          msg = "Enter an email address."
        }
        break;
      case "pass":
        if (!this.state.newUser.pass) {
          msg = "Enter a password."
        } else {
          msg = authServiceFactory().checkPWRequirements(this.state.newUser.pass);
        }
        break;
      case "confirmpass":
        if (!this.state.newUser.confirmpass) {
          msg = "Re-enter your password."
        } else if (this.state.newUser.confirmpass !== this.state.newUser.pass) {
          msg = "Password Mismatch.";
        }
        break;
      default:
        console.warn("Unknown input:", inputElem);
        return;
    }
    this.setState({errors: {...this.state.errors, [propName]: msg}});
  }

  private onKeyUp(event: React.KeyboardEvent) {
    if (event.key !== "Enter") { return; }
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    switch (propName) {
      case "firstname":
        this.lnameInput?.focus();
        break;
      case "lastname":
        if (this.state.newUser.emails && this.state.newUser.emails.length > 0) {
          this.passInput?.focus();
        } else {
          this.emailInput?.focus();
        }
        break;
      case "email":
        this.passInput?.focus();
        break;
      case "pass":
        this.confirmInput?.focus();
        break;
      case "confirmpass":
        this.doAccountCreation();
        break;
      default:
        return;
    }
  }

  async claimAsCurrentUser (): Promise<void> {
    try {
      const authService = authServiceFactory()
      await authService.claimInvite(this.state.token);

      this.setState({showExistingUserPostLogin: true});
    } catch (err) {
      console.error("Failed claiming invite:", err);
      this.setState({errMsg: (err as Error).message});
    }
  }

  async onOtherUserLogin (): Promise<void> {
    await authServiceFactory().claimInvite(this.state.token);
    this.setState({showExistingUserPostLogin: true, openLoginModal: false});
  }

  async finishedLogin (): Promise<void> {
    if (this.state.invite) {
      const memberships = await authServiceFactory().getAccountMemberships(true);
      const membership = memberships.find(
        m => m.accountID === this.state.invite?.accountID);
      if (membership) {
        await this.context.setAccount(membership);
      }
      if (this.state.invite.huddleGroupID) {
        this.props.navigate(`/group/${this.state.invite.huddleGroupID}/huddle`);
        return;
      }
    }
    this.props.navigate("/group");
  }

  async goToApp(): Promise<void> {
    try {
      await this.context.finishedLogin();
      await this.finishedLogin();
    } catch (err) {
      console.error("Failed finalizing login:", err);
      this.setState({errMsg: (err as Error).message});
    }
  }

  async doAccountCreation(): Promise<void> {
    if (this.state.saving) { return; }
    let msg = "";
    try {
      if (!this.state.invite) {
        this.setState({errMsg: "Missing Invite!"});
        return;
      }
      if (!this.state.newUser.firstname) {
        this.setState({errMsg: "Please, enter your first name."});
        return;
      }
      if (!this.state.newUser.lastname) {
        this.setState({errMsg: "Please, enter your last name."});
        return;
      }
      if (!this.state.newUser.emails || this.state.newUser.emails.length <= 0 ||
          this.state.newUser.emails.includes("")) {
        this.setState({errMsg: "Please, enter your email address."});
        return;
      }
      if (!this.state.newUser.pass) {
        this.setState({errMsg: "Please, choose a password."});
        return;
      }
      msg = authServiceFactory().checkPWRequirements(this.state.newUser.pass);
      if (msg) {
        this.setState({errMsg: msg});
        return;
      }
      if (this.state.newUser.pass !== this.state.newUser.confirmpass) {
        this.setState({errMsg: "Password Mismatch"});
        return;
      }

      this.setState({saving: true});
      const authService = authServiceFactory();
      await authService.createUser(
        this.state.newUser, this.state.invite.accountID, this.state.token);

      const memberships = await authService.getAccountMemberships(true);
      const userID = memberships.length > 0 ? memberships[0].userID : "";
      //await this.context.finishedLogin();

      this.setState({activeStep: this.lastStep, newUserID: userID});
    } catch (err) {
      console.error("Error creating account:", err);
      this.setState({errMsg: (err as Error).message});
    } finally {
      this.setState({saving: false});
    }
  }

  renderWelcome(): React.ReactElement<any, any> {
    const existingUser = this.state.existingUser;
    const companyName = this.state.invite?.accountname || "a company";

    return <div className="invite-welcome-container">
      <header>
        <h2>
        <span>Join </span>
        <span>{companyName}</span>
        <span> on {this.state.productName}</span>
        </h2>
      </header>
      <section className="invite-welcome-options">
        {existingUser && <article className="current-account text-info-block">
          <h3>Current Login:</h3>
          <div className="logged-in-user-description">
            <SCAvatar text={this.state.avatarText} />
            <div className="user-name">
              <span>{existingUser.firstname}</span>
              <span> {existingUser.lastname}</span>
            </div>
            {existingUser.emails.map(email => <div className="email" key={email}>{email}</div>)}
          </div>
          <p>
            <span>Click below to join </span>
            <span>{companyName}</span>
            <span> as {existingUser.firstname}.</span>
          </p>
          <div className="call-to-action">
            <Button variant="contained"
                disableRipple disableTouchRipple
                onClick={this.claimAsCurrentUser}>
              Join as {existingUser.firstname}
            </Button>
          </div>
        </article>}
        <article className="text-info-block">
          <h3>New to {this.state.productName}?</h3>
          <p>
            <Button variant="contained"
                disableRipple disableTouchRipple
                onClick={() => this.setState({doUserCreation: true})}>Sign Up</Button>
          </p>
          <div className="login-option">
            <div>{existingUser ?
              <span>Have another account?</span> :
              <span>Have an account?</span>}
            </div>
            <div>
              <Button
                disableRipple disableTouchRipple
                onClick={() => this.setState({openLoginModal: true})}>Log in</Button>
            </div>
          </div>
        </article>
      </section>
    </div>;
  }

  renderStepperFlow(): React.ReactElement<any, any> {
    return <Stepper activeStep={this.state.activeStep} orientation="vertical">
      <Step>
        <StepLabel>Create User</StepLabel>
        <StepContent>
          <form className="form-wrapper">
            <h2>Basic Info:</h2>
            <div className="form-row">
              <FormControl fullWidth>
                <TextField id="fname-input" label="First Name" name="firstname" required
                  type="text" size="small"
                  autoComplete="given-name"
                  error={!!this.state.errors.firstname}
                  helperText={this.state.errors.firstname}
                  variant="outlined"
                  value={this.state.newUser.firstname}
                  onChange={this.updateValue}
                  onBlur={this.onBlur}
                  onKeyUp={this.onKeyUp} />
              </FormControl>
            </div>
            <div className="form-row">
              <FormControl fullWidth>
                <TextField id="lastname-input" label="Last Name" type="text" name="lastname"
                  autoComplete="family-name" size="small"
                  required
                  error={!!this.state.errors.lastname}
                  helperText={this.state.errors.lastname}
                  variant="outlined"
                  value={this.state.newUser.lastname}
                  inputRef={input => { this.lnameInput = input; } }
                  onChange={this.updateValue}
                  onBlur={this.onBlur}
                  onKeyUp={this.onKeyUp} />
              </FormControl>
            </div>
            <div className="form-row">
              <FormControl fullWidth>
                <TextField id="email-input" label="Email" type="email" name="email"
                  autoComplete="username" size="small"
                  required
                  error={!!this.state.errors.emails}
                  helperText={this.state.errors.emails}
                  variant="outlined"
                  value={this.state.newUser.emails[0]}
                  inputRef={input => { this.emailInput = input; } }
                  onChange={this.updateValue}
                  onBlur={this.onBlur}
                  onKeyUp={this.onKeyUp} />
              </FormControl>
            </div>
            <h2>Password:</h2>
            <div className="password-rules">
              <p>Passwords must:</p>
              <ul>
                <li>be 10 characters or longer,</li>
                <li>contain one lower case letter,</li>
                <li>contain one capital letter,</li>
                <li>contain one number,</li>
                <li>contain one special character, <br />&mdash; or &mdash;</li>
                <li>be 24 characters or longer</li>
              </ul>
            </div>
            <div className="form-row">
              <FormControl fullWidth>
                <TextField id="pass-input" label="Create Password" name="pass"
                  type="password" size="small"
                  autoComplete="new-password"
                  required
                  error={!!this.state.errors.pass}
                  helperText={this.state.errors.pass}
                  variant="outlined"
                  value={this.state.newUser.pass}
                  inputRef={input => { this.passInput = input; } }
                  onChange={this.updateValue}
                  onBlur={this.onBlur}
                  onKeyPress={this.onKeyUp} />
              </FormControl>
            </div>
            <div className="form-row">
              <FormControl fullWidth>
                <TextField id="confirm-input" label="Confirm Password" name="confirmpass"
                  autoComplete="new-password" size="small"
                  required
                  type="password"
                  error={!!this.state.errors.confirmpass}
                  helperText={this.state.errors.confirmpass}
                  variant="outlined"
                  value={this.state.newUser.confirmpass}
                  inputRef={input => { this.confirmInput = input; } }
                  onChange={this.updateValue}
                  onBlur={this.onBlur}
                  onKeyPress={this.onKeyUp} />
              </FormControl>
            </div>
            <div className="button-row">
              <Button
                  disableRipple disableTouchRipple
                  onClick={() => this.setState({doUserCreation: false})}>Back</Button>
              <Button color="primary" variant="contained"
                  disableRipple disableTouchRipple
                  onClick={this.doAccountCreation}>
                Create
              </Button>
            </div>
          </form>
        </StepContent>
      </Step>
      <Step>
        <StepLabel>Multi-factor Authentication</StepLabel>
        <StepContent>
          <p>
            Multi-factor Authentication (MFA) adds additional security to your
            account, making it much more difficult for attackers to gain access
            to your account.
          </p>
          <div className="onboard-mfa-container">
            <MfaSetupComponent
              userID={this.state.newUserID}
              email={this.state.newUser.emails[0]}
              showInternalAuth={this.state.haveInternalAuthenticator}
              didStartOTP={() => this.setState({showingOTPView: true})}
              didClearOTP={() => this.setState({showingOTPView: false})} />
          </div>
          <div className="button-row">
            <Button color="primary"
              variant="contained"
              disableRipple disableTouchRipple
              disabled={this.state.showingOTPView}
              onClick={() => this.setState({activeStep: 2})}>Continue</Button>
          </div>
        </StepContent>
      </Step>
      <Step>
        <StepLabel>Finish Up</StepLabel>
        <StepContent>
          {this.renderFinish(false)}
        </StepContent>
      </Step>
    </Stepper>;
        /**/
  }

  renderFinish(withExisting: boolean): React.ReactElement<any, any> {
    return <div className="next-steps-container">
      <h2 key="header">All Set</h2>
      {withExisting &&
        <p>
          It may take up to 30 minutes for this Account to show in any other
          devices you have. Or you can log into those devices again to update
          them sooner.
        </p>
      }
      {this.state.isIOS &&
        <div className="iosapp-link-container">
          <p>Download the app:</p>
          <a className="applink" href="https://apps.apple.com/us/app/mercury-workspace/id1589508705">
            <img src="/img/appStoreBadgeBlack.svg" />
          </a>
          <p>or:</p>
        </div>
      }
      <div className="webapp-link-container">
        <p className="web-app-instructions">
          <span>Click below to continue to </span>
          <span>{this.state.invite?.accountname || "this company"}</span>
          <span> using our web app:</span>
        </p>
        <Button key="Button"
          disableRipple disableTouchRipple
          variant={this.state.isIOS ? undefined : "contained"} onClick={this.goToApp}>
          Continue with Web App
        </Button>
      </div>
    </div>;
  }

  render(): React.ReactElement<any, any> {
    if (this.state.invalidBrowser) {
      return <div className="main-section-inner onboarding-welcome-container">
        <h1>Welcome to {this.state.productName}</h1>
        <Alert
          variant="filled"
          severity="error">{this.state.errMsg}</Alert>
      </div>;
    } else if (this.state.invalidToken) {
      return this.renderBadToken();
    } else if (this.state.showExistingUserPostLogin) {
      return <div className="main-section-inner onboarding-welcome-container">
        <h1 className="welcome-heading">Welcome</h1>
        { this.state.errMsg && <Alert
          variant="filled"
          severity="error"
          onClick={() => this.setState({errMsg: ""})}>{this.state.errMsg}</Alert> }
        {this.renderFinish(true)}
      </div>;
    } else if (this.state.loading) {
      return <div className="main-section-inner onboarding-welcome-container">
        <h1>Welcome to {this.state.productName}</h1>
        <CircularProgress size="100" />
      </div>;
    }

    return <div className="main-section-inner onboarding-welcome-container">
      <h1 className="welcome-heading">Welcome</h1>
      { this.state.errMsg && <Alert
        variant="filled"
        severity="error"
        onClick={() => this.setState({errMsg: ""})}>{this.state.errMsg}</Alert> }

      { this.state.doUserCreation ?
          this.renderStepperFlow() :
          this.renderWelcome() }

      <LoginModal open={this.state.openLoginModal}
        onFinish={this.onOtherUserLogin}
        onClose={() => this.setState({openLoginModal: false}) } />
    </div>;
  }

  renderBadToken(): React.ReactElement<any, any> {
    return <div className="main-section-inner onboarding-welcome-container">
      <div className="invite-welcome-container">
        <h1>Welcome to {this.state.productName}</h1>
        <section className="invite-failure-container">
          <article className="text-info-block">
              <h3>Oops</h3>
              <p>{this.state.errMsg}</p>
              <div className="login-option">
                <div>Looking to Just Log In?</div>
                <div>
                  <Button disableRipple disableTouchRipple color="secondary"
                    onClick={() => this.props.navigate("/login")}>Log in</Button>
                </div>
              </div>
          </article>
        </section>
      </div>
    </div>;
  }
}
